import { adapterSelectors }    from '@core/store/adapter/adapter.selectors';
import { EntityAdapter }       from '@ngrx/entity';
import {
	createFeatureSelector,
	createSelector
}                              from '@ngrx/store';
import {
	ListOperations,
	ListOperationsIdentifierEnum
}                              from '@shared/list-operations/model/list-operations.model';
import { ListOperationsState } from '@shared/list-operations/store/list-operations.state';
import { LoggerService }       from '@shared/services/logger.service';


export function listOperationsSelectors(
	adapter: EntityAdapter<ListOperations>,
	storeId: string,
	logger: LoggerService
) {

	const listOperationsStateSelector = createFeatureSelector<ListOperationsState>( storeId );
	const adapterSelector = { ...adapterSelectors<ListOperations, ListOperationsState>( adapter, listOperationsStateSelector ) };


	const getListOperationsById = ( listOperationsList: ListOperations[], listOperationId: ListOperationsIdentifierEnum ): ListOperations => {
		return listOperationsList.find( ( listOperations: ListOperations ) => listOperations.id === listOperationId );
	};

	const selectListOperationsActiveFilters = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.activeFilters
		);

	const selectListOperationsById = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )
		);

	const selectListOperationsFilterIsInitialized = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.filterIsInitialized
		);

	const selectListOperationsHideFilter = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.hideFilter
		);

	const selectListOperationsHideSearch = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.hideSearch
		);

	const selectListOperationsHideSort = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.hideSort
		);

	const selectListOperationsPaging = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.paging
		);

	const selectListOperationsPagingIsInitialized = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.pagingIsInitialized
		);

	const selectListOperationsRenderFilter = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.renderFilter
		);

	const selectListOperationsRenderSearch = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.renderSearch
		);

	const selectListOperationsRenderSort = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.renderSort
		);

	const selectListOperationsSearch = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.search
		);

	const selectListOperationsSearchIsInitialized = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.searchIsInitialized
		);

	const selectListOperationsSort = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.sort
		);

	const selectListOperationsSortIsInitialized = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.sortIsInitialized
		);

	const selectListOperationsResetData = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.resetData
		);

	const selectListOperationsRestoreListOperationsData = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.restoreListOperationsData
		);

	const selectListOperationsConfigData = ( listOperationId: ListOperationsIdentifierEnum ) =>
		createSelector(
			adapterSelector?.all,
			( listOperationsList: ListOperations[] ) =>
				getListOperationsById( listOperationsList, listOperationId )?.configData
		);

	const selectActiveListOperationsId = createSelector(
		listOperationsStateSelector,
		( listOperationsState: ListOperationsState ) => listOperationsState?.activeListOperationsId
	);

	return {
		adapterSelector,

		// LIST OPERATIONS SPECIFIC SELECTORS
		selectActiveListOperationsId,
		selectAllListOperations: adapterSelector?.all,
		selectListOperationsActiveFilters,
		selectListOperationsById,
		selectListOperationsConfigData,
		selectListOperationsFilterIsInitialized,
		selectListOperationsHideFilter,
		selectListOperationsHideSearch,
		selectListOperationsHideSort,
		selectListOperationsPaging,
		selectListOperationsPagingIsInitialized,
		selectListOperationsRenderFilter,
		selectListOperationsRenderSearch,
		selectListOperationsRenderSort,
		selectListOperationsResetData,
		selectListOperationsRestoreListOperationsData,
		selectListOperationsSearch,
		selectListOperationsSearchIsInitialized,
		selectListOperationsSort,
		selectListOperationsSortIsInitialized
	};
}
